import { ReactNode, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

type SubPageHeaderProps = {
  backTo?: string;
  onBack?: () => void;
  icon?: ReactNode;
  badge?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  backPlacement?: 'start' | 'end';
};

export function SubPageHeader({ backTo, onBack, icon, badge, title, description, actions, backPlacement = 'start' }: SubPageHeaderProps) {
  const location = useLocation();
  const queryBackTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('backTo');
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [location.search]);
  const resolvedBackTo = queryBackTo || backTo;

  const renderBackButton = () => {
    if (onBack) {
      return (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
          Voltar
        </button>
      );
    }
    if (resolvedBackTo) {
      return (
        <Link
          to={resolvedBackTo}
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
          Voltar
        </Link>
      );
    }
    return null;
  };

  if (backPlacement === 'end') {
    return (
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {icon ? <span className="shrink-0 rounded-lg bg-primary-50 p-2 text-primary-700">{icon}</span> : null}
          <div className="min-w-0">
            {badge ? <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{badge}</p> : null}
            <h1 className="break-words text-2xl font-semibold text-gray-900">{title}</h1>
            {description ? <p className="text-sm text-gray-600">{description}</p> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
          {actions}
          {renderBackButton()}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          {renderBackButton()}
          {icon ? <span className="rounded-lg bg-primary-50 p-2 text-primary-700">{icon}</span> : null}
        </div>
        <div className="min-w-0">
          {badge ? <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{badge}</p> : null}
          <h1 className="break-words text-2xl font-semibold text-gray-900">{title}</h1>
          {description ? <p className="text-sm text-gray-600">{description}</p> : null}
        </div>
      </div>
      {actions ? (
        <div className="flex w-full flex-wrap items-center justify-start gap-3 sm:gap-4 lg:w-auto lg:max-w-full lg:flex-shrink-0 lg:justify-end">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
